'use client';

import { useState, useEffect, useRef } from 'react';
import { Play, Pause, ChevronLeft, ChevronRight, Volume2, Languages, Home, Maximize2, Minimize2, Sparkles, Star, Award, Trophy, RefreshCw, ArrowRight } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import Asset3DRenderer from '../3d-assets/Asset3DRenderer';
import StoryCompletionModal from './StoryCompletionModal';
import BilingualSubscriptionGate from './BilingualSubscriptionGate';
import ProUpsellModal from './ProUpsellModal';
import MagicalEffect from './MagicalEffect';
import PageAtmosphere from './PageAtmosphere';
import VisualWeatherEffect from './VisualWeatherEffect';
import EpicMomentEffect from './EpicMomentEffect';
import WordHighlightedText from './WordHighlightedText';
import LetterHighlightedText from './LetterHighlightedText';

interface BilingualPage {
  pageNum: number;
  imageUrl: string;
  text: string;
  translatedText: string;
  audioUrl: string;
  translatedAudioUrl: string;
  duration: number;
  soundEffectUrl?: string;
  isCover?: boolean;
  isEnding?: boolean;
  asset3D?: {
    assetId: string;
    glbUrl: string;
    usdzUrl?: string;
    posterUrl?: string;
    placement: 'center';
    motion: 'auto-rotate' | 'none';
    sizeHint: 'featured';
    note?: string;
  };
  magicalEffect?: {
    effectId: string;
    note?: string;
  };
  atmosphere?: {
    gradient: [string, string];
    mood: string;
    timeOfDay?: string;
    brightness: 'light' | 'medium' | 'dark';
  };
  visualEffect?: {
    type: 'rain' | 'snow' | 'sunshine' | 'lightning' | 'fog' | 'wind' | 'none';
    intensity: 'light' | 'medium' | 'heavy';
  };
  epicMoment?: {
    enabled: boolean;
    effect: 'burst' | 'cascade' | 'ripple' | 'fireworks' | 'lightning-strike';
    trigger: 'auto' | 'on-page-load';
  };
  // Audio Library System fields
  emotion?: string;
  objectSfxUrl?: string;
  bgmUrl?: string;
  bgmVolume?: number;
  wordTimings?: Array<{ w: string; start: number; end: number }>;
  translatedWordTimings?: Array<{ w: string; start: number; end: number }>; // NEW: For target language word highlighting
  // Learning mode fields
  letterTimings?: Array<{ letter: string; start: number; end: number; wordIndex: number }>;
  translatedLetterTimings?: Array<{ letter: string; start: number; end: number; wordIndex: number }>;
}

interface BilingualBookData {
  id: string;
  title: string;
  coverImageUrl: string | null;
  pages: BilingualPage[];
  totalDuration: number;
  sourceLanguage: string;
  targetLanguage: string;
  montessoriMode?: boolean;
  ageCategory?: string;
  isLearningContent?: boolean;
}

interface BilingualAudioBookViewerProps {
  audioBookId: string;
}

type PlayMode = 'source' | 'target' | 'alternating';

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Mandarin',
  ar: 'Arabic',
  pl: 'Polish',
  lt: 'Lithuanian',
};

// Helper function to clamp values between min and max
function clamp(min: number, val: number, max: number) {
  return Math.min(Math.max(min, val), max);
}

export default function BilingualAudioBookViewer({ audioBookId }: BilingualAudioBookViewerProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const [bookData, setBookData] = useState<BilingualBookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  // ✅ LEARNING FIX: Always start in alternating mode for best learning experience
  const [playMode, setPlayMode] = useState<PlayMode>('alternating');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCover, setShowCover] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([]);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [showStoryCompletion, setShowStoryCompletion] = useState(false);
  const [learningStats, setLearningStats] = useState<any>(null);
  const [watchStartTime, setWatchStartTime] = useState<number>(Date.now());
  
  // NEW: Learning mode state
  const [isLearningMode, setIsLearningMode] = useState(false);
  
  // NEW: Subscription & access control state
  const [showSubscriptionGate, setShowSubscriptionGate] = useState(false);
  const [showProUpsellModal, setShowProUpsellModal] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  
  const sourceAudioRef = useRef<HTMLAudioElement>(null);
  const targetAudioRef = useRef<HTMLAudioElement>(null);
  const bgmAudioRef = useRef<HTMLAudioElement>(null);
  const sfxAudioRef = useRef<HTMLAudioElement>(null); // ✅ NEW: For sound effects
  const shouldPlayAfterTransition = useRef(false);
  const pageStartTimeRef = useRef<number>(0);
  const MIN_3D_PAGE_DURATION = 10000; // 10 seconds minimum for 3D pages
  
  const [currentTime, setCurrentTime] = useState(0);
  const currentTimeRef = useRef(0); // Performance: Store time in ref to avoid re-renders
  const [activeLetterIndex, setActiveLetterIndex] = useState(-1); // Performance: Only update when letter changes
  const [bgmLoaded, setBgmLoaded] = useState(false);
  // ✅ FIX: Track which audio source is currently active (critical for alternating mode)
  const [activeAudioSource, setActiveAudioSource] = useState<'source' | 'target' | null>(null);

  useEffect(() => {
    fetchBookData();
    checkAccessControl();
    const newParticles = Array.from({ length: 25 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 6
    }));
    setParticles(newParticles);
  }, [audioBookId]);

  // ✅ PERFORMANCE FIX: Use requestAnimationFrame instead of timeupdate for smooth 60fps highlighting
  // This reduces re-renders from 250/sec to ~5-10/sec (only when letter index actually changes)
  useEffect(() => {
    const sourceAudio = sourceAudioRef.current;
    const targetAudio = targetAudioRef.current;
    
    if (!sourceAudio || !targetAudio || !bookData) return;
    
    let animationFrameId: number;
    let lastLetterIndex = -1;
    
    const updateTime = () => {
      // Get current page data safely
      const currentPageData = bookData?.pages[currentPageIndex];
      if (!currentPageData) {
        animationFrameId = requestAnimationFrame(updateTime);
        return;
      }
      
      // Determine which audio is currently active
      let currentAudio = null;
      let currentLetterTimings = null;
      
      if (playMode === 'source' || (playMode === 'alternating' && !sourceAudio.paused && !sourceAudio.ended)) {
        currentAudio = sourceAudio;
        currentLetterTimings = currentPageData.letterTimings;
        setActiveAudioSource('source');
      } else if (playMode === 'target' || (playMode === 'alternating' && !targetAudio.paused && !targetAudio.ended)) {
        currentAudio = targetAudio;
        currentLetterTimings = currentPageData.translatedLetterTimings;
        setActiveAudioSource('target');
      }
      
      if (currentAudio) {
        // Update ref (no re-render) - used by WordHighlightedText
        currentTimeRef.current = currentAudio.currentTime;
        setCurrentTime(currentAudio.currentTime); // Still update state for non-performance-critical uses
        
        // Only update state when active letter INDEX changes (massive performance gain)
        if (currentLetterTimings && isPlaying) {
          const newIndex = findActiveLetterIndex(currentTimeRef.current, currentLetterTimings);
          
          if (newIndex !== lastLetterIndex) {
            lastLetterIndex = newIndex;
            setActiveLetterIndex(newIndex);
          }
        }
      } else if (lastLetterIndex !== -1) {
        lastLetterIndex = -1;
        setActiveLetterIndex(-1);
      }
      
      animationFrameId = requestAnimationFrame(updateTime);
    };
    
    // Start the animation loop
    animationFrameId = requestAnimationFrame(updateTime);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [bookData, currentPageIndex, isPlaying, playMode]); // ✅ FIX: Use bookData & currentPageIndex instead of currentPage

  // Check if learning content requires Pro subscription
  useEffect(() => {
    if (bookData?.isLearningContent && !isPro && !isOwner) {
      setShowProUpsellModal(true);
    } else {
      setShowProUpsellModal(false); // FIXED: Close modal if user IS Pro or owner
    }
  }, [bookData, isPro, isOwner]);

  // BGM cross-fade on page change - ONLY when BGM URL changes (club-style continuity)
  useEffect(() => {
    if (!bookData || showCover) return;
    
    const currentPage = bookData.pages[currentPageIndex];
    const bgmAudio = bgmAudioRef.current;
    if (!bgmAudio) return;
    
    const targetVolume = currentPage?.bgmVolume || 0.14;
    const newBgmUrl = currentPage?.bgmUrl;
    
    // CRITICAL FIX: Check if BGM URL actually changed
    // If same URL, just continue playing (don't restart)
    const currentSrc = bgmAudio.src;
    const isSameBgm = newBgmUrl && currentSrc && currentSrc.includes(newBgmUrl.split('/').pop() || '');
    
    if (newBgmUrl && !isSameBgm) {
      // BGM URL CHANGED - Do smooth crossfade
      const wasPlaying = !bgmAudio.paused;
      
      // Fade out current BGM smoothly
      if (wasPlaying) {
        const fadeOutInterval = setInterval(() => {
          if (bgmAudio.volume > 0.01) {
            bgmAudio.volume = Math.max(0, bgmAudio.volume - 0.02);
          } else {
            clearInterval(fadeOutInterval);
            
            // Switch to new BGM and fade in
            bgmAudio.src = newBgmUrl;
            bgmAudio.loop = true;
            bgmAudio.volume = 0;
            bgmAudio.load();
            
            // Fade in new BGM
            bgmAudio.play().then(() => {
              const fadeInInterval = setInterval(() => {
                if (bgmAudio.volume < targetVolume - 0.01) {
                  bgmAudio.volume = Math.min(targetVolume, bgmAudio.volume + 0.02);
                } else {
                  bgmAudio.volume = targetVolume;
                  clearInterval(fadeInInterval);
                }
              }, 50);
            }).catch(console.error);
          }
        }, 50);
      } else {
        // Not playing, just load new BGM
        bgmAudio.src = newBgmUrl;
        bgmAudio.volume = targetVolume;
        bgmAudio.loop = true;
        bgmAudio.load();
      }
    } else if (newBgmUrl && isSameBgm) {
      // SAME BGM - Just adjust volume if needed (keep playing continuously)
      if (Math.abs(bgmAudio.volume - targetVolume) > 0.01) {
        // Smooth volume adjustment
        const volumeDiff = targetVolume - bgmAudio.volume;
        const step = volumeDiff / 20; // 20 steps for smooth transition
        let count = 0;
        const volumeInterval = setInterval(() => {
          count++;
          if (count < 20) {
            bgmAudio.volume += step;
          } else {
            bgmAudio.volume = targetVolume;
            clearInterval(volumeInterval);
          }
        }, 50);
      }
      console.log(`[BGM] Continuing same music across page change (${newBgmUrl.split('/').pop()})`);
    } else if (!newBgmUrl && !bgmAudio.paused) {
      // No BGM on this page - fade out current music
      const fadeOutInterval = setInterval(() => {
        if (bgmAudio.volume > 0.01) {
          bgmAudio.volume = Math.max(0, bgmAudio.volume - 0.02);
        } else {
          bgmAudio.pause();
          bgmAudio.volume = 0;
          clearInterval(fadeOutInterval);
        }
      }, 50);
    }
    
    return () => {
      // Don't stop music on cleanup - let it continue to next page
      // Only stop when component unmounts completely
    };
  }, [currentPageIndex, bookData, showCover]);

  // ESC key handler for fullscreen
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isFullscreen]);

  async function fetchBookData() {
    try {
      const response = await fetch(`/api/audio-book/${audioBookId}`);
      if (!response.ok) throw new Error('Failed to fetch book');
      
      const data = await response.json();
      const pages = data.pagesData;
      
      // ✅ FIX: Only enable letter mode if BOTH conditions met:
      // Determine highlighting mode based on spellingMode field
      // 1. Check if book has explicit spellingMode setting
      // 2. For learning content: respect the spellingMode field (word/letter/none)
      // 3. Default to word highlighting for regular books
      const hasLetterTimings = pages.some((p: any) => p.letterTimings && p.letterTimings.length > 0);
      const spellingMode = data.spellingMode || 'word'; // Default to word mode
      
      if (data.isLearningContent && spellingMode === 'letter' && hasLetterTimings) {
        setIsLearningMode(true);
        console.log('[Bilingual Viewer] 🎓 Learning mode: LETTER spelling - Letter-by-letter highlighting active');
      } else if (data.isLearningContent && spellingMode === 'word') {
        setIsLearningMode(false);
        console.log('[Bilingual Viewer] 🎓 Learning mode: WORD spelling - Word-by-word highlighting active');
      } else {
        setIsLearningMode(false);
        console.log('[Bilingual Viewer] 📖 Regular mode - Word highlighting active');
      }
      
      // ✅ LEARNING FIX: Force alternating mode for learning books (best for education)
      if (data.isLearningContent) {
        setPlayMode('alternating');
        console.log('[Bilingual Viewer] 🎓 Learning book detected - LOCKED to alternating mode for automatic language switching');
      }
      
      setBookData({
        id: data.id,
        title: data.title,
        coverImageUrl: data.coverImageUrl,
        pages,
        totalDuration: data.totalDuration || 0,
        sourceLanguage: data.sourceLanguage || 'en',
        targetLanguage: data.targetLanguage || 'es',
        montessoriMode: data.montessoriMode,
        ageCategory: data.ageCategory,
        isLearningContent: data.isLearningContent,
      });
    } catch (error) {
      console.error('Error fetching book:', error);
    } finally {
      setLoading(false);
    }
  }

  async function checkAccessControl() {
    try {
      // Fetch first 3 featured samples
      const samplesRes = await fetch('/api/audio-book/samples');
      const samplesData = await samplesRes.json();
      const freeSampleIds = (samplesData.audioBooks || []).map((b: any) => b.id);
      
      // Fetch book data
      const bookRes = await fetch(`/api/audio-book/${audioBookId}`);
      if (!bookRes.ok) {
        setLoading(false);
        return;
      }
      const book = await bookRes.json();
      
      // Fetch user account
      const accountRes = await fetch('/api/account');
      const accountData = accountRes.ok ? await accountRes.json() : null;
      
      const isBookOwner = accountData && book.userId === accountData.id;
      const isInFreeBooks = freeSampleIds.includes(audioBookId);
      const hasSubscription = accountData?.isSubscriber || 
                              accountData?.subscriptionPlan === 'AMAYA_PLUS' || 
                              accountData?.subscriptionPlan === 'AMAYA_PRO';
      
      setIsOwner(isBookOwner);
      setIsPro(accountData?.subscriptionPlan === 'AMAYA_PRO');
      setIsSubscriber(hasSubscription);
      
      // Access: subscribers OR owners OR featured samples
      const hasAccess = hasSubscription || isBookOwner || isInFreeBooks;
      
      setCanAccess(hasAccess);
      // DON'T show gate immediately - let users see the book first for SEO & marketing!
      // Gate will be triggered when they click Begin Adventure or Play button
    } catch (error) {
      console.error('Error checking access:', error);
      // Don't show gate on error either - let user try to interact first
    }
  }

  // 🎯 PERFORMANCE: Predictive preload system for 3D assets (N+1 & N+2)
  useEffect(() => {
    if (!bookData?.pages) return;
    
    // Preload next 2 pages' 3D assets for instant load
    const upcomingPages = bookData.pages.slice(
      currentPageIndex + 1,
      currentPageIndex + 3
    );
    
    upcomingPages.forEach((page: any) => {
      if (page.asset3D?.glbUrl) {
        // ✅ CRITICAL FIX: Use fetch with CORS mode instead of <link> prefetch
        // Link prefetch doesn't properly handle CORS for cross-origin resources
        fetch(page.asset3D.glbUrl, { 
          mode: 'cors',
          credentials: 'omit',
          priority: 'low' as any // Hint: low priority background fetch
        }).then(response => {
          if (response.ok) {
            console.log('[Preload] ✅ 3D asset preloaded:', page.asset3D!.assetId);
          }
        }).catch(err => {
          console.log('[Preload] ⚠️  Preload failed (not critical):', page.asset3D!.assetId);
        });
        
        // Preload USDZ for AR (iOS)
        if (page.asset3D.usdzUrl) {
          fetch(page.asset3D.usdzUrl, {
            mode: 'cors',
            credentials: 'omit',
            priority: 'low' as any
          }).catch(() => {}); // Silent fail
        }
      }
    });
    
    // Cleanup handled automatically by browser
  }, [bookData, currentPageIndex]);

  const currentPage = bookData?.pages[currentPageIndex];

  // Load and prepare audio when page changes
  useEffect(() => {
    if (!currentPage || showCover || isPageTransitioning) return;

    const sourceAudio = sourceAudioRef.current;
    const targetAudio = targetAudioRef.current;

    if (!sourceAudio || !targetAudio) return;

    // ✅ CRITICAL FIX: Validate audio URLs before loading (fixes page 11 issue)
    const pageNumber = currentPageIndex + 1;
    
    if (!currentPage.audioUrl) {
      console.error(`❌ Page ${pageNumber}: Missing audioUrl!`);
      console.error('Page data:', currentPage);
      alert(`Audio missing on page ${pageNumber}. This page may not have been generated correctly.`);
      return;
    }

    if (!currentPage.translatedAudioUrl) {
      console.error(`❌ Page ${pageNumber}: Missing translatedAudioUrl!`);
      console.error('Page data:', currentPage);
      alert(`Translated audio missing on page ${pageNumber}. This page may not have been generated correctly.`);
      return;
    }

    console.log(`📄 Loading page ${pageNumber} audio...`);
    console.log(`  Source: ${currentPage.audioUrl.substring(0, 60)}...`);
    console.log(`  Target: ${currentPage.translatedAudioUrl.substring(0, 60)}...`);

    sourceAudio.src = currentPage.audioUrl;
    targetAudio.src = currentPage.translatedAudioUrl;
    sourceAudio.load();
    targetAudio.load();

    // ✅ NEW: Load sound effects if available (supports both soundEffectUrl and objectSfxUrl)
    const sfx = sfxAudioRef.current;
    const effectUrl = currentPage.soundEffectUrl || currentPage.objectSfxUrl;
    if (sfx && effectUrl) {
      console.log('[SFX] Loading sound effect:', effectUrl);
      sfx.src = effectUrl;
      sfx.loop = true; // Loop ambient sound effects
      sfx.volume = 0.20; // 20% volume for balanced audio mix
      sfx.load();
    } else if (sfx) {
      // No sound effect on this page - reset
      sfx.pause();
      sfx.currentTime = 0;
      sfx.src = '';
    }

    if (shouldPlayAfterTransition.current) {
      // Start playing immediately (no delay)
      setIsPlaying(true);
      shouldPlayAfterTransition.current = false;
    }
  }, [currentPage, showCover, isPageTransitioning, currentPageIndex]);

  // Handle audio playback based on play mode
  useEffect(() => {
    if (!currentPage || !isPlaying || showCover || isPageTransitioning) {
      const sourceAudio = sourceAudioRef.current;
      const targetAudio = targetAudioRef.current;
      const bgm = bgmAudioRef.current;
      const sfx = sfxAudioRef.current; // ✅ NEW: Pause SFX too
      if (sourceAudio) sourceAudio.pause();
      if (targetAudio) targetAudio.pause();
      if (bgm) bgm.pause();
      if (sfx) sfx.pause(); // ✅ NEW
      return;
    }

    const sourceAudio = sourceAudioRef.current;
    const targetAudio = targetAudioRef.current;
    const bgm = bgmAudioRef.current;

    if (!sourceAudio || !targetAudio) return;

    // Play BGM if available (no delays)
    if (bgm && currentPage?.bgmUrl) {
      bgm.play().catch(console.error);
    }

    // ✅ NEW: Play sound effects if available
    const sfx = sfxAudioRef.current;
    const effectUrl = currentPage.soundEffectUrl || currentPage.objectSfxUrl;
    if (sfx && effectUrl) {
      console.log('[SFX] Playing sound effect:', effectUrl);
      sfx.play().catch(console.error);
    }

    if (playMode === 'source') {
      sourceAudio.play().catch(console.error);
      setActiveAudioSource('source');
    } else if (playMode === 'target') {
      targetAudio.play().catch(console.error);
      setActiveAudioSource('target');
    } else if (playMode === 'alternating') {
      // ✅ FIX: Start with source audio
      sourceAudio.play().catch(console.error);
      setActiveAudioSource('source');
      
      sourceAudio.onended = () => {
        // ✅ FIX: Reset currentTime when switching to target audio
        setCurrentTime(0);
        setActiveAudioSource('target');
        
        // Add a brief pause between source and target audio
        setTimeout(() => {
          targetAudio.play().catch(console.error);
        }, 400); // 400ms pause between languages for better comprehension
      };
      
      targetAudio.onended = () => {
        // Add delay before auto-advancing to next page for learning
        const elapsedTime = Date.now() - pageStartTimeRef.current;
        
        // For pages with 3D objects, ensure minimum display time
        if (currentPage?.asset3D && elapsedTime < MIN_3D_PAGE_DURATION) {
          const remainingTime = MIN_3D_PAGE_DURATION - elapsedTime + 1200;
          console.log(`⏳ 3D page - waiting ${remainingTime}ms before advancing`);
          setTimeout(() => {
            handleNext();
          }, remainingTime);
        } else {
          setTimeout(() => {
            handleNext();
          }, 1200); // 1.2 second pause after completing both audio tracks
        }
      };
    }

    return () => {
      sourceAudio.pause();
      targetAudio.pause();
      sourceAudio.onended = null;
      targetAudio.onended = null;
    };
  }, [currentPage, isPlaying, playMode, showCover, isPageTransitioning]);

  // ✅ CRITICAL FIX: Add audio error listeners to diagnose page-specific failures
  useEffect(() => {
    const sourceAudio = sourceAudioRef.current;
    const targetAudio = targetAudioRef.current;

    if (!sourceAudio || !targetAudio) return;

    const handleSourceError = (e: Event) => {
      const pageNumber = currentPageIndex + 1;
      console.error(`❌ Source audio error on page ${pageNumber}:`, {
        errorCode: sourceAudio.error?.code,
        errorMessage: sourceAudio.error?.message,
        src: sourceAudio.src,
        readyState: sourceAudio.readyState,
        networkState: sourceAudio.networkState
      });
      
      // Show user-friendly error
      alert(`Audio failed to load on page ${pageNumber} (source language). The audio file may be missing or inaccessible. Please try refreshing the page or report this issue.`);
      setIsPlaying(false);
    };

    const handleTargetError = (e: Event) => {
      const pageNumber = currentPageIndex + 1;
      console.error(`❌ Target audio error on page ${pageNumber}:`, {
        errorCode: targetAudio.error?.code,
        errorMessage: targetAudio.error?.message,
        src: targetAudio.src,
        readyState: targetAudio.readyState,
        networkState: targetAudio.networkState
      });
      
      // Show user-friendly error
      alert(`Translated audio failed to load on page ${pageNumber}. The audio file may be missing or inaccessible. Please try refreshing the page or report this issue.`);
      setIsPlaying(false);
    };

    sourceAudio.addEventListener('error', handleSourceError);
    targetAudio.addEventListener('error', handleTargetError);

    return () => {
      sourceAudio.removeEventListener('error', handleSourceError);
      targetAudio.removeEventListener('error', handleTargetError);
    };
  }, [currentPageIndex]);

  // Track page start time for minimum display duration on 3D pages
  useEffect(() => {
    pageStartTimeRef.current = Date.now();
  }, [currentPageIndex]);

  function handlePlayPause() {
    // Check if user has access before allowing play
    if (!canAccess && !showCover) {
      setShowSubscriptionGate(true);
      return;
    }
    setIsPlaying(!isPlaying);
  }

  function handleBeginAdventure() {
    // Check if user has access before starting the story
    if (!canAccess) {
      setShowSubscriptionGate(true);
      return;
    }
    // Start playing from current page (cover page)
    setShowCover(false);
    setIsPlaying(true);
    setWatchStartTime(Date.now());
  }

  // Track completion when user finishes the story
  async function trackCompletion() {
    if (!bookData || !session?.user?.id) {
      // Show confetti even if not logged in
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      return;
    }
    
    const timeSpent = Math.round((Date.now() - watchStartTime) / 1000); // seconds
    
    // Get the actual age category from the book, with fallback to 3-4
    const ageCategory = bookData.ageCategory || '3-4';
    
    // Log warning if age category is missing
    if (!bookData.ageCategory) {
      console.warn(`⚠️ Audiobook ${bookData.id} is missing ageCategory field. Using default "3-4".`);
    }
    
    try {
      const response = await fetch('/api/learning/progress/complete-again', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audioBookId: bookData.id,
          ageCategory, // Use book's actual age category
          timeSpent,
          completedAt: new Date().toISOString(),
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setLearningStats(data);
        setShowCompletionScreen(true);
      } else {
        console.error('Failed to track completion');
      }
    } catch (error) {
      console.error('Error tracking completion:', error);
    }
    
    // Show confetti regardless of tracking success
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000);
  }

  function handleNext() {
    if (!bookData || isPageTransitioning) return;
    if (currentPageIndex < bookData.pages.length - 1) {
      const wasPlaying = isPlaying;
      
      // Start page transition with fade effect
      setIsPageTransitioning(true);
      setIsPlaying(false); // Pause during transition
      
      // Wait for fade-out, then change page, then fade-in
      setTimeout(() => {
      setCurrentPageIndex(currentPageIndex + 1);
        if (wasPlaying) {
          shouldPlayAfterTransition.current = true;
        }
        
        // Complete transition after fade-in
        setTimeout(() => {
          setIsPageTransitioning(false);
        }, 600); // Fade-in duration
      }, 600); // Fade-out duration
    } else {
      // Last page reached - track completion and show celebration!
      trackCompletion();
    }
  }

  function handlePrevious() {
    if (currentPageIndex > 0 && !isPageTransitioning) {  // ✅ FIX: Allow going back to page 0
      const wasPlaying = isPlaying;
      
      // Start page transition with fade effect
      setIsPageTransitioning(true);
      setIsPlaying(false); // Pause during transition
      
      // Wait for fade-out, then change page, then fade-in
      setTimeout(() => {
      setCurrentPageIndex(currentPageIndex - 1);
        if (wasPlaying) {
          shouldPlayAfterTransition.current = true;
        }
        
        // Complete transition after fade-in
        setTimeout(() => {
          setIsPageTransitioning(false);
        }, 600); // Fade-in duration
      }, 600); // Fade-out duration
    }
  }

  function handleFullscreenToggle() {
    setIsFullscreen(!isFullscreen);
    
    // Telemetry
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'fullscreen_toggle', {
        book_id: audioBookId,
        new_state: !isFullscreen ? 'fullscreen' : 'normal',
        page_num: currentPageIndex + 1
      });
    }
  }

  async function handleContinueStory() {
    if (!bookData) return;
    
    try {
      // Get the last 2 pages for better context
      const lastPages = bookData.pages.slice(-2);
      const contextText = lastPages.map(p => p.text).join(' ');
      
      const continuePrompt = `Continue the story "${bookData.title}". 
Recent events: ${contextText.slice(0, 300)}... 

Please create the NEXT chapter with new exciting developments that naturally follow from where the story left off. Keep the same magical tone and style, and continue the adventure in a meaningful way.`;
      
      sessionStorage.setItem('continueStoryPrompt', continuePrompt);
      sessionStorage.setItem('originalStoryTitle', bookData.title);
      sessionStorage.setItem('isContinuation', 'true');
      
      // Redirect to create page (it will auto-trigger generation)
      window.location.href = '/audio-book/create';
    } catch (error) {
      console.error('Error continuing story:', error);
      alert('Failed to continue story. Please try again.');
    }
  }

  async function handleTranslateStory(targetLanguage: string) {
    if (!bookData) return;
    
    try {
      const response = await fetch('/api/audio-book/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalBookId: audioBookId,
          targetLanguage,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Failed to create translation');
        return;
      }

      const data = await response.json();
      
      // Notify navbar to refresh credits
      window.dispatchEvent(new Event('creditsChanged'));
      
      // Redirect to the new bilingual book
      window.location.href = `/audio-book/${data.translationId}`;
    } catch (error) {
      console.error('Error creating translation:', error);
      alert('Failed to create translation. Please try again.');
    }
  }

  if (loading) {
    return (
      <div style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        overflow: 'hidden',
      }}>
        {/* Animated Background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(102, 126, 234, 0.15) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(118, 75, 162, 0.15) 0%, transparent 50%)',
          animation: 'pulseGlow 8s ease-in-out infinite',
        }} />
        
        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}>
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: '3px',
                height: '3px',
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `floatParticle ${Math.random() * 10 + 15}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`,
                boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
              }}
            />
          ))}
        </div>

        {/* Main Content */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2rem',
        }}>
          {/* Animated Icon Container */}
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {/* Glow Rings */}
            <div style={{
              position: 'absolute',
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              border: '3px solid rgba(102, 126, 234, 0.3)',
              animation: 'ringPulse 2s ease-in-out infinite',
            }} />
            <div style={{
              position: 'absolute',
              width: '110px',
              height: '110px',
              borderRadius: '50%',
              border: '2px solid rgba(118, 75, 162, 0.3)',
              animation: 'ringPulse 2s ease-in-out infinite 0.5s',
            }} />

            {/* Glass Icon Container */}
            <div style={{
              position: 'relative',
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              animation: 'gentleBounce 2s ease-in-out infinite',
            }}>
              <Languages 
                size={40} 
                color="white" 
                style={{
                  filter: 'drop-shadow(0 4px 20px rgba(255, 255, 255, 0.5))',
                  animation: 'spin 3s linear infinite',
                }}
              />
            </div>
          </div>

          {/* Loading Text */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: 800,
              color: 'white',
              margin: 0,
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              letterSpacing: '0.02em',
            }}>
              Loading Bilingual Story
            </h2>
            
            {/* Animated Dots */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              height: '24px',
            }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    animation: `dotPulse 1.5s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.5)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes floatParticle {
            0%, 100% {
              transform: translateY(0) translateX(0);
              opacity: 0;
            }
            10% {
              opacity: 0.5;
            }
            50% {
              transform: translateY(-100vh) translateX(20px);
              opacity: 0.8;
            }
            90% {
              opacity: 0.5;
            }
            100% {
              transform: translateY(-200vh) translateX(40px);
              opacity: 0;
            }
          }
          
          @keyframes ringPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.15);
              opacity: 0.6;
            }
          }
          
          @keyframes gentleBounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
          
          @keyframes dotPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.5;
            }
            50% {
              transform: scale(1.5);
              opacity: 1;
            }
          }
          
          @keyframes pulseGlow {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
        `}</style>
      </div>
    );
  }

  if (!bookData || !currentPage) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '3rem',
          borderRadius: '32px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
          <h2 style={{ color: '#2D3748', marginBottom: '1rem' }}>Book not found</h2>
          <a href="/dashboard" style={{
            color: '#667eea',
            textDecoration: 'none',
            fontWeight: 700,
            fontSize: '1.125rem',
          }}>
            Go to Dashboard →
          </a>
        </div>
      </div>
    );
  }

  const sourceLangName = LANGUAGE_NAMES[bookData.sourceLanguage] || bookData.sourceLanguage;
  const targetLangName = LANGUAGE_NAMES[bookData.targetLanguage] || bookData.targetLanguage;

  // MAIN LEARNING EXPERIENCE - 2026 Award-Winning Design
  return (
    <div style={{
      height: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 35%, #f093fb 70%, #4facfe 100%)',
      backgroundSize: '400% 400%',
      animation: 'gradientFlow 20s ease infinite',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Ambient Glow Effect */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'radial-gradient(circle at 30% 40%, rgba(102, 126, 234, 0.15) 0%, transparent 50%)',
        pointerEvents: 'none',
        animation: 'pulseGlow 8s ease-in-out infinite',
      }} />

      {/* Confetti Effect on Last Page */}
      {showConfetti && (
        <div style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 9999,
        }}>
          {[...Array(50)].map((_, i) => (
            <div
              key={`confetti-${i}`}
              style={{
                position: 'absolute',
                left: `${Math.random() * 100}%`,
                top: '-20px',
                width: `${8 + Math.random() * 8}px`,
                height: `${8 + Math.random() * 8}px`,
                background: ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#FFD700', '#FF69B4'][Math.floor(Math.random() * 6)],
                borderRadius: Math.random() > 0.5 ? '50%' : '0',
                animation: `confettiFall ${2 + Math.random() * 2}s linear forwards`,
                animationDelay: `${Math.random() * 0.5}s`,
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
          ))}
        </div>
      )}

      {/* Compact Header */}
      <div style={{
        padding: 'clamp(0.5rem, 1vh, 0.625rem) clamp(1rem, 2vw, 1.25rem)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        position: 'relative',
        zIndex: 10,
        flexShrink: 0,
        background: 'rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(30px) saturate(160%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <a
            href="/dashboard"
          aria-label="Go to dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
            gap: '0.375rem',
            padding: '0.375rem 0.75rem',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '12px',
              color: 'white',
              textDecoration: 'none',
            fontWeight: 700,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
          }}
        >
          <Home size={16} strokeWidth={2.5} />
          <span>Home</span>
        </a>

        {/* Centered Bilingual Title - Compact */}
          <div style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
          gap: '0.5rem',
          maxWidth: '600px',
        }}>
        <h1 style={{
            fontSize: 'clamp(0.8125rem, 1.9vw, 0.9375rem)',
            fontWeight: 800,
          color: 'white',
            margin: 0,
            textShadow: '0 1px 8px rgba(0,0,0,0.2)',
          textAlign: 'center',
          lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }}>
          {bookData.title}
        </h1>
          {playMode === 'alternating' && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.25rem 0.5rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              backdropFilter: 'blur(15px)',
              borderRadius: '50px',
              fontSize: '0.5625rem',
              fontWeight: 800,
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              animation: 'pulseGlow 2.5s ease-in-out infinite',
              boxShadow: '0 2px 10px rgba(16, 185, 129, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              flexShrink: 0,
            }}>
              <Languages size={10} strokeWidth={3} />
              <span style={{ whiteSpace: 'nowrap' }}>Learning</span>
            </div>
          )}
        </div>

        {/* Compact Page Counter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.375rem 0.75rem',
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px)',
          borderRadius: '50px',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'white',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
        }}>
          <Star size={11} fill="white" color="white" style={{ flexShrink: 0 }} />
          <span>{currentPage.pageNum}/{bookData.pages.length}</span>
        </div>
      </div>

      {/* Compact Language Selector */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
        gap: '0.375rem',
        padding: '0 1rem 0.5rem',
        flexShrink: 0,
        }}>
          {[
          { mode: 'source' as PlayMode, label: sourceLangName, icon: null },
          { mode: 'alternating' as PlayMode, label: 'Both', icon: Languages },
          { mode: 'target' as PlayMode, label: targetLangName, icon: null },
        ].map((option) => {
          const Icon = option.icon;
          // ✅ LEARNING FIX: For learning books, disable single-language modes
          const isLearningBook = bookData?.isLearningContent;
          const isDisabled = isLearningBook && option.mode !== 'alternating';
          
          return (
            <button
              key={option.mode}
              onClick={() => {
                // ✅ LEARNING FIX: Prevent changing away from alternating mode for learning books
                if (isDisabled) return;
                setPlayMode(option.mode);
                setIsPlaying(false);
              }}
            aria-label={`Switch to ${option.label} mode`}
            disabled={isDisabled}
              style={{
              padding: '0.375rem 0.875rem',
                background: playMode === option.mode
                ? 'rgba(255, 255, 255, 0.9)'
                : isDisabled
                ? 'rgba(255, 255, 255, 0.05)'  // ✅ Dimmed for disabled buttons
                : 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(20px)',
              border: playMode === option.mode 
                ? '1.5px solid rgba(255, 255, 255, 0.8)' 
                : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '50px',
              color: playMode === option.mode 
                ? '#667eea' 
                : isDisabled 
                ? 'rgba(255, 255, 255, 0.3)'  // ✅ Dimmed text for disabled
                : 'white',
              fontWeight: playMode === option.mode ? 800 : 600,
                cursor: isDisabled ? 'not-allowed' : 'pointer',  // ✅ Not-allowed cursor for disabled
              transition: 'all 0.3s ease',
              fontSize: '0.75rem',
              boxShadow: playMode === option.mode 
                ? '0 3px 12px rgba(255,255,255,0.25)' 
                : '0 1px 4px rgba(0,0,0,0.06)',
              whiteSpace: 'nowrap',
              minWidth: '68px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              opacity: isDisabled ? 0.4 : 1,  // ✅ Visual feedback for disabled state
            }}
            onMouseEnter={(e) => {
              // ✅ LEARNING FIX: No hover effect for disabled buttons
              if (!isPageTransitioning && !isDisabled) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = playMode === option.mode 
                  ? '0 4px 16px rgba(255,255,255,0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDisabled) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = playMode === option.mode 
                  ? '0 3px 12px rgba(255,255,255,0.25)' 
                  : '0 1px 4px rgba(0,0,0,0.06)';
              }
            }}
            >
              {Icon && <Icon size={16} strokeWidth={2.5} />}
              <span>{option.label}</span>
            </button>
          );
        })}
        </div>

      {/* Main Content - 65/35 Grid Layout: Image LEFT, Text RIGHT */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '65% 35%',
        gap: '1.25rem',
        padding: '0 1.25rem 1rem',
        minHeight: 0,
        overflow: 'hidden',
      }}>
        {/* LEFT: Image Container (65%) - FIXED: Single container, no nested columns */}
        <div style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: 0,
        }}>
          {/* Story Image Container - All effects and controls inside here */}
          <div style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '30px',
            overflow: 'visible',  // ✅ CRITICAL: Allow 3D buttons to show
            boxShadow: isFullscreen 
              ? '0 0 80px rgba(102, 126, 234, 0.6)'
              : '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}>
            {/* Page Atmosphere (z-index: 1) */}
            {currentPage?.atmosphere && (
              <PageAtmosphere
                gradient={currentPage.atmosphere.gradient}
                mood={currentPage.atmosphere.mood}
                timeOfDay={currentPage.atmosphere.timeOfDay}
                brightness={currentPage.atmosphere.brightness}
              />
            )}

            <img
              src={currentPage.imageUrl}
              alt={`Page ${currentPage.pageNum}`}
              style={{
                  width: '100%',
                  height: '100%',
                objectFit: 'contain',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.15))',
                  opacity: isPageTransitioning ? 0 : 1,
                  transform: isPageTransitioning ? 'scale(0.95)' : 'scale(1)',
                  position: 'relative',
                  zIndex: 10,
                }}
                className="story-image-hover"
              />

              {/* Featured 3D Asset (if present) (z-index: 100) */}
              {currentPage?.asset3D && (
                <>
                  <Asset3DRenderer
                    key={`3d-bilingual-normal-${currentPageIndex}-${currentPage.asset3D.assetId}`}
                    glbUrl={currentPage.asset3D.glbUrl}
                    usdzUrl={currentPage.asset3D.usdzUrl}
                    assetId={currentPage.asset3D.assetId}
                    placement="center"
                    montessoriMode={false}
                    isPlaying={isPlaying}
                    objectSfxUrl={currentPage.objectSfxUrl}
                    isFirstAssetPage={bookData?.pages?.findIndex((p: any) => p.asset3D) === currentPageIndex}
                  />
                </>
              )}

              {/* Visual Weather Effect (z-index: 55-60) */}
              {currentPage?.visualEffect && currentPage.visualEffect.type !== 'none' && (
                <VisualWeatherEffect
                  type={currentPage.visualEffect.type}
                  intensity={currentPage.visualEffect.intensity}
                />
              )}

              {/* Magical Effect (z-index: 5 or 50) */}
              {currentPage?.magicalEffect && (
                <MagicalEffect
                  effectId={currentPage.magicalEffect.effectId}
                />
              )}

              {/* Epic Moment Effect (z-index: 70) */}
              {currentPage?.epicMoment?.enabled && (
                <EpicMomentEffect
                  effect={currentPage.epicMoment.effect}
                  trigger={currentPage.epicMoment.trigger}
                />
              )}

              {/* BEGIN LEARNING Button Overlay (z-index: 200) - Shows on cover page */}
              {showCover && currentPage?.isCover && !showSubscriptionGate && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(8px)',
                  zIndex: 200,
                  animation: 'fadeIn 0.5s ease-out',
                }}>
                  <button
                    onClick={handleBeginAdventure}
                    aria-label={isLearningMode ? "Begin learning journey" : "Begin bilingual adventure"}
                    style={{
                      padding: 'clamp(1.5rem, 3vh, 2rem) clamp(3rem, 6vw, 4rem)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '3px solid rgba(255, 255, 255, 0.6)',
                      borderRadius: '60px',
                      color: '#667eea',
                      fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                      fontWeight: 900,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'clamp(1rem, 2vw, 1.5rem)',
                      boxShadow: '0 20px 50px rgba(255, 255, 255, 0.3), inset 0 2px 0 rgba(255, 255, 255, 1)',
                      transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      position: 'relative',
                      overflow: 'hidden',
                      animation: 'buttonPulse 2s ease-in-out infinite',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px) scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 25px 60px rgba(255, 255, 255, 0.4), inset 0 2px 0 rgba(255, 255, 255, 1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 20px 50px rgba(255, 255, 255, 0.3), inset 0 2px 0 rgba(255, 255, 255, 1)';
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.3), transparent)',
                      animation: 'shimmer 3s ease-in-out infinite',
                    }} />
                    
                    <Play 
                      size={56} 
                      fill="white" 
                      strokeWidth={2.5} 
                      style={{ 
                        flexShrink: 0,
                        position: 'relative',
                        zIndex: 1,
                      }} 
                    />
                    
                    <span style={{ position: 'relative', whiteSpace: 'nowrap', zIndex: 1 }}>
                      {isLearningMode ? 'Begin Learning' : 'Begin Adventure'}
                    </span>
                  </button>
                </div>
              )}

              {/* Subtle Vignette */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.05) 100%)',
                pointerEvents: 'none',
              }} />

              {/* Compact Audio Indicator */}
              {isPlaying && (
                <div style={{
                  position: 'absolute',
                  top: '0.5rem',
                  left: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.375rem 0.625rem',
                  background: 'rgba(16, 185, 129, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  boxShadow: '0 2px 10px rgba(16, 185, 129, 0.4)',
                  animation: 'audioPlaying 1.5s ease-in-out infinite',
                  border: '1px solid rgba(255,255,255,0.25)',
                }}>
                  <Volume2 size={13} color="white" strokeWidth={2.5} />
                  <div style={{
                    display: 'flex',
                    gap: '2px',
                    alignItems: 'flex-end',
                  }}>
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        style={{
                          width: '2px',
                          height: `${6 + Math.random() * 6}px`,
                          background: 'white',
                          borderRadius: '2px',
                          animation: `audioBars 0.8s ease-in-out infinite`,
                          animationDelay: `${i * 0.1}s`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

            {/* Control Bar Overlay - ABSOLUTE positioned at bottom (z-index: 100) */}
            <div style={{
              position: 'absolute',
              bottom: isFullscreen ? '3.5rem' : '4.5rem',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: isFullscreen ? '0.5rem' : '0.75rem',
              padding: isFullscreen ? '0.5rem 0.75rem' : '0.75rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
              backdropFilter: 'blur(25px) saturate(180%)',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
              zIndex: 100,
            }}>
              {/* Previous Button */}
              <button
                onClick={handlePrevious}
                disabled={currentPageIndex === 0 || isPageTransitioning}
                style={{
                  padding: isFullscreen ? '0.5rem' : '0.625rem',
                  background: currentPageIndex === 0 
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  opacity: currentPageIndex === 0 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (currentPageIndex > 0) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <ChevronLeft size={isFullscreen ? 20 : 24} strokeWidth={3} />
              </button>

              {/* Play/Pause Button - FIX: Circular design matching audio-book-viewer-unified */}
              <button
                onClick={handlePlayPause}
                disabled={isPageTransitioning}
                aria-label={isPlaying ? 'Pause narration' : 'Play narration'}
                style={{
                  padding: '0.875rem',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                  border: '2px solid rgba(255, 255, 255, 0.6)',
                  borderRadius: '50%',  // ✅ FIX: Circular button
                  color: '#667eea',  // ✅ FIX: Purple icon color
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 20px rgba(255, 255, 255, 0.3)',
                  minWidth: '48px',  // ✅ FIX: Ensure circular shape
                  minHeight: '48px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 100%)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 8px 28px rgba(255, 255, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.3)';
                }}
              >
                {/* ✅ FIX: Icon-only, no text label */}
                {isPlaying ? (
                  <Pause size={28} strokeWidth={3} fill="currentColor" />
                ) : (
                  <Play size={28} strokeWidth={3} fill="currentColor" style={{ marginLeft: '2px' }} />
                )}
              </button>

              {/* Next Button */}
              <button
                onClick={handleNext}
                disabled={currentPageIndex >= bookData.pages.length - 1 || isPageTransitioning}
                style={{
                  padding: isFullscreen ? '0.5rem' : '0.625rem',
                  background: currentPageIndex >= bookData.pages.length - 1
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: currentPageIndex >= bookData.pages.length - 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  opacity: currentPageIndex >= bookData.pages.length - 1 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (currentPageIndex < bookData.pages.length - 1) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <ChevronRight size={isFullscreen ? 20 : 24} strokeWidth={3} />
              </button>

              {/* Zoom/Fullscreen Button */}
              <button
                onClick={handleFullscreenToggle}
                aria-label="Toggle fullscreen"
                style={{
                  padding: '0.5rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.25s ease',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.35)',
                  minWidth: '36px',
                  minHeight: '36px',
                  justifyContent: 'center',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.35)';
                }}
              >
                <Maximize2 size={18} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT: Compact Text Cards (35%) - FIX: Better overflow handling */}
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.625rem',
          minHeight: 0,
          maxHeight: '100%',  // ✅ FIX: Constrain height to prevent overflow
          overflow: 'auto',  // ✅ FIX: Allow scrolling if content too tall
          paddingRight: '3px',
          paddingTop: '0.5rem',
          minWidth: '280px',  // ✅ FIX: Minimum width to keep text readable
        }}>
          {/* Bilingual Cards Container */}
            <div style={{
            // ALWAYS vertical stacking for better learning (English on top, Spanish below)
            display: 'flex',
            flexDirection: 'column',
            gap: '0.625rem',
            flex: 1,
            minHeight: 0,
          }}>
            {/* Compact Source Language Card */}
            {(playMode === 'source' || playMode === 'alternating') && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.45)',
                backdropFilter: 'blur(40px) saturate(170%)',
                borderRadius: '16px',
                padding: '1rem 1.125rem',
                boxShadow: '0 6px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.35), inset 0 1px 1px rgba(255,255,255,0.3)',
                border: '1.5px solid rgba(255, 255, 255, 0.45)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                position: 'relative',
                overflow: 'auto',
                opacity: isPageTransitioning ? 0 : 1,
                transform: isPageTransitioning ? 'translateY(8px)' : 'translateY(0)',
                transition: 'all 0.5s ease-out',
              }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '1.5px solid rgba(102, 126, 234, 0.15)',
                  flexShrink: 0,
              }}>
                <div style={{
                    padding: '0.375rem 0.875rem',
                    background: activeAudioSource === 'source' && isPlaying
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'  // ✅ GREEN when actively playing
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '50px',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    color: 'white',
                  textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    boxShadow: activeAudioSource === 'source' && isPlaying
                      ? '0 4px 16px rgba(16, 185, 129, 0.6)'  // ✅ STRONGER glow when playing
                      : '0 2px 8px rgba(102, 126, 234, 0.35)',
                    animation: activeAudioSource === 'source' && isPlaying ? 'pulse 2s ease-in-out infinite' : 'none',
                }}>
                  {activeAudioSource === 'source' && isPlaying ? '▶️ ' : ''}{sourceLangName}
                </div>
                  {(playMode === 'source' || (playMode === 'alternating' && isPlaying)) && (
                  <div style={{
                      width: '7px',
                      height: '7px',
                    borderRadius: '50%',
                    background: '#667eea',
                    animation: 'pulse 1.5s ease-in-out infinite',
                      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.2)',
                  }} />
                )}
              </div>

              <p style={{
                  fontSize: 'clamp(0.9375rem, 2.1vw, 1.0625rem)',
                  lineHeight: 1.75,
                  color: '#1a202c',
                margin: 0,
                  fontWeight: 500,
                  // Removed textShadow to allow WordHighlightedText karaoke effects to shine through
                  letterSpacing: '0.012em',
                  textAlign: 'left',
                  flex: 1,
                  whiteSpace: 'pre-wrap',  // CRITICAL: Preserve spaces in text!
              }}>
                {isLearningMode ? (
                  <LetterHighlightedText
                    text={currentPage.text}
                    letterTimings={currentPage.letterTimings}
                    activeLetterIndex={activeAudioSource === 'source' ? activeLetterIndex : -1}
                    isPlaying={playMode === 'source' || playMode === 'alternating'}
                    activeLanguage="source"
                    showOnlySpelling={false}
                    enhancedKidsMode={bookData?.isLearningContent || false}
                  />
                ) : (
                  <WordHighlightedText
                    text={currentPage.text}
                    wordTimings={currentPage.wordTimings}
                    currentTime={activeAudioSource === 'source' ? currentTime : undefined}
                    isPlaying={playMode === 'source' || playMode === 'alternating'}
                  />
                )}
              </p>
            </div>
            )}

            {/* Compact Target Language Card */}
            {(playMode === 'target' || playMode === 'alternating') && (
            <div style={{
                background: 'rgba(248, 249, 250, 0.5)',
                backdropFilter: 'blur(40px) saturate(170%)',
                borderRadius: '16px',
                padding: '1rem 1.125rem',
                boxShadow: '0 6px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.35), inset 0 1px 1px rgba(255,255,255,0.3)',
                border: '1.5px solid rgba(255, 255, 255, 0.45)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                position: 'relative',
                overflow: 'auto',
                opacity: isPageTransitioning ? 0 : 1,
                transform: isPageTransitioning ? 'translateY(8px)' : 'translateY(0)',
                transition: 'all 0.5s ease-out 0.08s',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '1.5px solid rgba(118, 75, 162, 0.15)',
                  flexShrink: 0,
              }}>
                <div style={{
                    padding: '0.375rem 0.875rem',
                    background: activeAudioSource === 'target' && isPlaying
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'  // ✅ GREEN when actively playing
                      : 'linear-gradient(135deg, #764ba2 0%, #f093fb 100%)',
                    borderRadius: '50px',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    color: 'white',
                  textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    boxShadow: activeAudioSource === 'target' && isPlaying
                      ? '0 4px 16px rgba(16, 185, 129, 0.6)'  // ✅ STRONGER glow when playing
                      : '0 2px 8px rgba(118, 75, 162, 0.35)',
                    animation: activeAudioSource === 'target' && isPlaying ? 'pulse 2s ease-in-out infinite' : 'none',
                }}>
                  {activeAudioSource === 'target' && isPlaying ? '▶️ ' : ''}{targetLangName}
                </div>
                  {(playMode === 'target' || (playMode === 'alternating' && isPlaying)) && (
                  <div style={{
                      width: '7px',
                      height: '7px',
                    borderRadius: '50%',
                    background: '#764ba2',
                    animation: 'pulse 1.5s ease-in-out infinite',
                      boxShadow: '0 0 0 3px rgba(118, 75, 162, 0.2)',
                  }} />
                )}
              </div>

              <p style={{
                  fontSize: 'clamp(0.9375rem, 2.1vw, 1.0625rem)',
                  lineHeight: 1.75,
                  color: '#1a202c',
                margin: 0,
                  fontWeight: 500,
                  // Removed textShadow to allow WordHighlightedText karaoke effects to shine through
                  letterSpacing: '0.012em',
                  textAlign: 'left',
                  flex: 1,
                  whiteSpace: 'pre-wrap',  // CRITICAL: Preserve spaces in text!
              }}>
                {isLearningMode ? (
                  <LetterHighlightedText
                    text={currentPage.translatedText}
                    letterTimings={currentPage.translatedLetterTimings}
                    activeLetterIndex={activeAudioSource === 'target' ? activeLetterIndex : -1}
                    isPlaying={playMode === 'target' || playMode === 'alternating'}
                    activeLanguage="target"
                    showOnlySpelling={false}
                    enhancedKidsMode={bookData?.isLearningContent || false}
                  />
                ) : (
                  <WordHighlightedText
                    text={currentPage.translatedText}
                    wordTimings={currentPage.translatedWordTimings || currentPage.wordTimings || []}
                    currentTime={activeAudioSource === 'target' ? currentTime : undefined}
                    isPlaying={playMode === 'target' || playMode === 'alternating'}
                    className=""
                  />
                )}
              </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden Audio Elements */}
      <audio ref={sourceAudioRef} preload="auto" />
      <audio ref={targetAudioRef} preload="auto" />
      <audio 
        ref={bgmAudioRef} 
        preload="auto" 
        loop
        onCanPlayThrough={() => setBgmLoaded(true)}
      />
      <audio 
        ref={sfxAudioRef} 
        preload="auto" 
        loop
      />
      
      {/* Completion Screen Modal - 2026 Award-Winning Design */}
      {showCompletionScreen && learningStats && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.15) 0%, rgba(0, 0, 0, 0.95) 100%)',
          backdropFilter: 'blur(30px) saturate(180%)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(1rem, 3vw, 2rem)',
          animation: 'fadeIn 0.5s ease-out',
        }}>
          {/* Floating Particles Background */}
          <div style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}>
            {[...Array(30)].map((_, i) => (
              <div
                key={`particle-${i}`}
                style={{
                  position: 'absolute',
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${2 + Math.random() * 4}px`,
                  height: `${2 + Math.random() * 4}px`,
                  background: `rgba(255, 255, 255, ${0.3 + Math.random() * 0.5})`,
                  borderRadius: '50%',
                  animation: `floatParticle ${5 + Math.random() * 5}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 3}s`,
                  boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)',
                }}
              />
            ))}
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 35%, rgba(240, 147, 251, 0.95) 70%, rgba(79, 172, 254, 0.95) 100%)',
            backgroundSize: '200% 200%',
            borderRadius: '40px',
            padding: 'clamp(2.5rem, 6vw, 4rem) clamp(2rem, 5vw, 3rem)',
            maxWidth: '700px',
            width: '100%',
            boxShadow: '0 40px 120px rgba(102, 126, 234, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            animation: 'scaleIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), gradientFlow 8s ease infinite',
            position: 'relative',
            overflow: 'hidden',
            border: '2px solid rgba(255, 255, 255, 0.15)',
          }}>
            {/* Shine Effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
              animation: 'shimmer 3s ease-in-out infinite',
              pointerEvents: 'none',
            }} />

            {/* Animated Background Orbs */}
            <div style={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              borderRadius: '40px',
              pointerEvents: 'none',
            }}>
              {[...Array(5)].map((_, i) => (
                <div
                  key={`completion-orb-${i}`}
                  style={{
                    position: 'absolute',
                    width: `${150 + i * 50}px`,
                    height: `${150 + i * 50}px`,
                    background: `radial-gradient(circle, rgba(255, 255, 255, ${0.15 - i * 0.02}) 0%, transparent 70%)`,
                    borderRadius: '50%',
                    left: `${i * 20}%`,
                    top: `${i * 15}%`,
                    animation: `floatOrb${(i % 3) + 1} ${18 + i * 2}s ease-in-out infinite`,
                    filter: 'blur(50px)',
                  }}
                />
              ))}
            </div>

            {/* Content */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Trophy Icon with Glow */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '2rem',
                animation: 'gentleBounce 2.5s ease-in-out infinite',
                position: 'relative',
              }}>
                {/* Trophy Glow Rings */}
                <div style={{
                  position: 'absolute',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)',
                  animation: 'pulse 3s ease-in-out infinite',
                  filter: 'blur(20px)',
                }} />
                <div style={{
                  position: 'absolute',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%)',
                  animation: 'pulse 2s ease-in-out infinite 0.5s',
                  filter: 'blur(15px)',
                }} />
                
                {/* Trophy with Enhanced Shadow */}
                <div style={{
                  position: 'relative',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  borderRadius: '50%',
                  padding: '1.5rem',
                  boxShadow: '0 0 40px rgba(255, 215, 0, 0.6), 0 10px 30px rgba(0, 0, 0, 0.3), inset 0 2px 0 rgba(255, 255, 255, 0.4)',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                }}>
                  <Trophy size={56} color="white" strokeWidth={2.5} style={{
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                  }} />
                  
                  {/* Sparkle Effects */}
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={`sparkle-${i}`}
                      style={{
                        position: 'absolute',
                        width: '8px',
                        height: '8px',
                        background: 'white',
                        borderRadius: '50%',
                        top: `${15 + i * 20}%`,
                        left: i % 2 === 0 ? '-10px' : 'auto',
                        right: i % 2 === 1 ? '-10px' : 'auto',
                        boxShadow: '0 0 15px rgba(255, 255, 255, 0.9)',
                        animation: `twinkle ${1 + i * 0.3}s ease-in-out infinite`,
                        animationDelay: `${i * 0.2}s`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Title with Enhanced Typography */}
              <h2 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #FFFFFF 0%, #E0E7FF 50%, #FFFFFF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textAlign: 'center',
                marginBottom: '1rem',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.5)',
                animation: 'fadeInDown 0.8s ease-out, titleGlow 2s ease-in-out infinite',
                letterSpacing: '0.02em',
                lineHeight: 1.2,
              }}>
                Story Completed! 🎉
              </h2>

              {/* Mastery Badge with Enhanced Design */}
              {learningStats.recommendation?.badge && (
                <div style={{
                  textAlign: 'center',
                  marginBottom: '2rem',
                  animation: 'fadeInUp 1s ease-out 0.3s both',
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    padding: '0.75rem 1.75rem',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.15) 100%)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    borderRadius: '50px',
                    fontSize: '1.25rem',
                    fontWeight: 900,
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.05em',
                  }}>
                    {learningStats.recommendation.badge}
                  </div>
                </div>
              )}

              {/* Stats Grid with Enhanced Design */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 'clamp(0.875rem, 2vw, 1.25rem)',
                marginBottom: '2rem',
                animation: 'fadeInUp 1s ease-out 0.5s both',
              }}>
                {/* Times Watched */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%)',
                  backdropFilter: 'blur(25px) saturate(180%)',
                  borderRadius: '24px',
                  padding: 'clamp(1.25rem, 3vw, 1.75rem)',
                  textAlign: 'center',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                }}
                >
                  {/* Shimmer Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                    animation: 'shimmer 4s ease-in-out infinite',
                  }} />
                  
                  <div style={{
                    fontSize: 'clamp(2rem, 5vw, 2.75rem)',
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginBottom: '0.5rem',
                    textShadow: '0 2px 10px rgba(255, 255, 255, 0.3)',
                    lineHeight: 1,
                  }}>
                    {learningStats.progress.completionCount}<span style={{ 
                      fontSize: '60%', 
                      opacity: 0.7,
                      fontWeight: 700 
                    }}> / {learningStats.progress.recommendedReps}</span>
                  </div>
                  <div style={{
                    fontSize: 'clamp(0.8125rem, 2vw, 0.9375rem)',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                  }}>
                    Times Watched
                  </div>
                </div>

                {/* Mastery Level with Progress Bar */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%)',
                  backdropFilter: 'blur(25px) saturate(180%)',
                  borderRadius: '24px',
                  padding: 'clamp(1.25rem, 3vw, 1.75rem)',
                  textAlign: 'center',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                }}
                >
                  {/* Shimmer Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                    animation: 'shimmer 4s ease-in-out infinite 0.5s',
                  }} />
                  
                  <div style={{
                    fontSize: 'clamp(2rem, 5vw, 2.75rem)',
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginBottom: '0.5rem',
                    textShadow: '0 2px 10px rgba(255, 255, 255, 0.3)',
                    lineHeight: 1,
                  }}>
                    {Math.round(learningStats.progress.masteryScore)}%
                  </div>
                  <div style={{
                    fontSize: 'clamp(0.8125rem, 2vw, 0.9375rem)',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                  }}>
                    Mastery Level
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{
                    marginTop: '0.75rem',
                    height: '6px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(learningStats.progress.masteryScore, 100)}%`,
                      background: 'linear-gradient(90deg, #FFFFFF 0%, #FFD700 100%)',
                      borderRadius: '10px',
                      boxShadow: '0 0 10px rgba(255, 215, 0, 0.8)',
                      animation: 'expandWidth 1.5s ease-out 0.8s both',
                    }} />
                  </div>
                </div>

                {/* Learning Time */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%)',
                  backdropFilter: 'blur(25px) saturate(180%)',
                  borderRadius: '24px',
                  padding: 'clamp(1.25rem, 3vw, 1.75rem)',
                  textAlign: 'center',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                }}
                >
                  {/* Shimmer Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                    animation: 'shimmer 4s ease-in-out infinite 1s',
                  }} />
                  
                  <div style={{
                    fontSize: 'clamp(2rem, 5vw, 2.75rem)',
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginBottom: '0.5rem',
                    textShadow: '0 2px 10px rgba(255, 255, 255, 0.3)',
                    lineHeight: 1,
                  }}>
                    {Math.round(learningStats.progress.totalTimeSpent / 60)}m
                  </div>
                  <div style={{
                    fontSize: 'clamp(0.8125rem, 2vw, 0.9375rem)',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                  }}>
                    Total Time
                  </div>
                </div>
              </div>

              {/* Encouragement Message with Enhanced Design */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.08) 100%)',
                backdropFilter: 'blur(25px) saturate(180%)',
                borderRadius: '28px',
                padding: 'clamp(1.5rem, 4vw, 2.25rem)',
                marginBottom: '2rem',
                border: '2px solid rgba(255, 255, 255, 0.35)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                animation: 'fadeInUp 1s ease-out 0.7s both',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Decorative Icon */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  fontSize: '2.5rem',
                  opacity: 0.15,
                  animation: 'pulse 3s ease-in-out infinite',
                }}>
                  👏
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '1rem',
                  right: '1rem',
                  fontSize: '2.5rem',
                  opacity: 0.15,
                  animation: 'pulse 3s ease-in-out infinite 1.5s',
                }}>
                  ⭐
                </div>
                
                <p style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
                  lineHeight: 1.8,
                  color: 'white',
                  margin: 0,
                  textAlign: 'center',
                  fontWeight: 600,
                  whiteSpace: 'pre-line',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  letterSpacing: '0.01em',
                  position: 'relative',
                  zIndex: 1,
                }}>
                  {learningStats.recommendation?.encouragementMessage}
                </p>
              </div>

              {/* Action Buttons with Enhanced Design */}
              <div style={{
                display: 'flex',
                gap: 'clamp(0.875rem, 2vw, 1.25rem)',
                flexDirection: 'row',
                flexWrap: 'wrap',
                animation: 'fadeInUp 1s ease-out 0.9s both',
              }} className="completion-buttons">
                {/* Repeat Story Button */}
                <button
                  onClick={() => {
                    setShowCompletionScreen(false);
                    setCurrentPageIndex(0);
                    setShowCover(true);
                    setWatchStartTime(Date.now());
                    setIsPlaying(false);
                  }}
                  style={{
                    flex: 1,
                    padding: 'clamp(1rem, 3vw, 1.25rem) clamp(1.5rem, 4vw, 2rem)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.2) 100%)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    border: '2.5px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '20px',
                    color: 'white',
                    fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
                    fontWeight: 900,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.625rem',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0.3) 100%)';
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 16px 48px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.7)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.2) 100%)';
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  }}
                >
                  {/* Button Shine Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    animation: 'shimmer 3s ease-in-out infinite',
                  }} />
                  
                  <RefreshCw size={22} strokeWidth={3} style={{ position: 'relative', zIndex: 1 }} />
                  <span style={{ position: 'relative', zIndex: 1 }}>Repeat Story</span>
                </button>

                {/* New Story Options Button - Featured */}
                <button
                  onClick={() => {
                    setShowCompletionScreen(false);
                    setShowStoryCompletion(true);
                  }}
                  style={{
                    flex: 1,
                    padding: 'clamp(1rem, 3vw, 1.25rem) clamp(1.5rem, 4vw, 2rem)',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: '2.5px solid rgba(255, 255, 255, 0.6)',
                    borderRadius: '20px',
                    color: 'white',
                    fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
                    fontWeight: 900,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.625rem',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 10px 40px rgba(16, 185, 129, 0.4)',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(16, 185, 129, 0.6)';
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                    e.currentTarget.style.boxShadow = '0 10px 40px rgba(16, 185, 129, 0.4)';
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  }}
                >
                  {/* Button Shine Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    animation: 'shimmer 3s ease-in-out infinite',
                  }} />
                  
                  <Sparkles size={22} strokeWidth={3} style={{ position: 'relative', zIndex: 1 }} />
                  <span style={{ position: 'relative', zIndex: 1 }}>New Adventure</span>
                </button>

                {/* Continue Learning Button - Premium CTA */}
                <button
                  onClick={() => {
                    window.location.href = '/dashboard';
                  }}
                  style={{
                    flex: 1,
                    padding: 'clamp(1rem, 3vw, 1.25rem) clamp(1.5rem, 4vw, 2rem)',
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 50%, #FFFFFF 100%)',
                    backgroundSize: '200% 200%',
                    border: '2.5px solid rgba(255, 255, 255, 0.8)',
                    borderRadius: '20px',
                    color: '#667eea',
                    fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
                    fontWeight: 900,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.625rem',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 10px 40px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(102, 126, 234, 0.1)',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #E0E7FF 50%, #FFFFFF 100%)';
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(255, 255, 255, 0.6), 0 0 0 2px rgba(102, 126, 234, 0.3)';
                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                    e.currentTarget.style.color = '#4f46e5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 50%, #FFFFFF 100%)';
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 10px 40px rgba(255, 255, 255, 0.4), 0 0 0 1px rgba(102, 126, 234, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                    e.currentTarget.style.color = '#667eea';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  }}
                >
                  {/* Button Shine Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.2), transparent)',
                    animation: 'shimmer 3s ease-in-out infinite 1s',
                  }} />
                  
                  {/* Glow Pulse Effect */}
                  <div style={{
                    position: 'absolute',
                    inset: '-2px',
                    background: 'linear-gradient(135deg, transparent, rgba(102, 126, 234, 0.4), transparent)',
                    borderRadius: '20px',
                    filter: 'blur(8px)',
                    opacity: 0.6,
                    animation: 'pulse 2s ease-in-out infinite',
                    pointerEvents: 'none',
                  }} />
                  
                  <span style={{ position: 'relative', zIndex: 1 }}>Continue Learning</span>
                  <ArrowRight size={22} strokeWidth={3} style={{ position: 'relative', zIndex: 1 }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Modal - Immersive Zoom Experience */}
      {isFullscreen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          animation: 'fadeIn 0.5s ease-out',
          overflow: 'hidden',
        }}>
          {/* Ambient Glow Background */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 50% 40%, rgba(102, 126, 234, 0.12) 0%, transparent 70%)',
            animation: 'pulseGlow 12s ease-in-out infinite',
            pointerEvents: 'none',
          }} />

          {/* Minimal Exit Button - Top Right */}
              <button
            onClick={() => setIsFullscreen(false)}
            aria-label="Exit fullscreen"
                style={{
              position: 'absolute',
              top: 'clamp(0.875rem, 2vh, 1.25rem)',
              right: 'clamp(0.875rem, 2vw, 1.25rem)',
              width: 'clamp(40px, 8vw, 48px)',
              height: 'clamp(40px, 8vh, 48px)',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(30px)',
              border: '1.5px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '50%',
                  color: 'white',
              cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 3px 16px rgba(0,0,0,0.35)',
              zIndex: 1000,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.transform = 'scale(1.08) rotate(90deg)';
              e.currentTarget.style.boxShadow = '0 5px 24px rgba(0,0,0,0.45)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
              e.currentTarget.style.boxShadow = '0 3px 16px rgba(0,0,0,0.35)';
            }}
          >
            <Minimize2 size={20} strokeWidth={2.5} />
              </button>

          {/* Page Indicator - Top Left */}
          <div style={{
            position: 'absolute',
            top: 'clamp(0.875rem, 2vh, 1.25rem)',
            left: 'clamp(0.875rem, 2vw, 1.25rem)',
            padding: '0.375rem 0.875rem',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(30px)',
            borderRadius: '50px',
            border: '1.5px solid rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            zIndex: 1000,
            boxShadow: '0 3px 16px rgba(0,0,0,0.35)',
          }}>
            <Star size={13} fill="white" color="white" />
            <span style={{
              color: 'white',
              fontSize: 'clamp(0.75rem, 1.7vw, 0.875rem)',
              fontWeight: 800,
              letterSpacing: '0.02em',
            }}>
              {currentPage.pageNum} / {bookData.pages.length}
            </span>
            </div>

          {/* Main Content - Large Image Focus */}
          <div className="fullscreen-layout" style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '80fr 20fr',
            gridTemplateRows: '1fr',  // ✅ FIX: Define row height to constrain image
            gap: 'clamp(0.75rem, 1.5vw, 1rem)',
            padding: 'clamp(3.5rem, 7vh, 4.5rem) clamp(1rem, 2vw, 1.5rem) clamp(0.75rem, 1.5vh, 1rem)',
            minHeight: 0,
            maxHeight: '100%',  // ✅ FIX: Prevent overflow
            overflow: 'hidden',
            position: 'relative',
            zIndex: 10,
          }}>
            {/* LEFT: Large Image - 80% of Screen */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              overflow: 'visible',  // FIX: Changed from 'hidden' to allow 3D rotation buttons to show
              minHeight: 0,  // ✅ FIX: Allow flex shrinking
              maxHeight: '100%',  // ✅ FIX: Constrain to grid cell
            }}>
              {/* Page Atmosphere (z-index: 1) */}
              {currentPage?.atmosphere && (
                <PageAtmosphere
                  gradient={currentPage.atmosphere.gradient}
                  mood={currentPage.atmosphere.mood}
                  timeOfDay={currentPage.atmosphere.timeOfDay}
                  brightness={currentPage.atmosphere.brightness}
                />
              )}

              {/* Dramatic Glow Effect */}
              <div style={{
                position: 'absolute',
                inset: '-60px',
                background: 'radial-gradient(circle, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.15) 40%, transparent 70%)',
                filter: 'blur(80px)',
                animation: 'pulseGlow 6s ease-in-out infinite',
                pointerEvents: 'none',
              }} />

              <img
                src={currentPage.imageUrl}
                alt={`Page ${currentPage.pageNum}`}
                style={{
                  maxWidth: '100%',  // ✅ FIX: Use max-width instead of width for proper scaling
                  maxHeight: '100%',  // ✅ FIX: Use max-height instead of height for proper scaling
                  width: 'auto',  // ✅ FIX: Allow natural aspect ratio
                  height: 'auto',  // ✅ FIX: Allow natural aspect ratio
                  objectFit: 'contain',
                  borderRadius: '20px',
                  filter: 'drop-shadow(0 30px 80px rgba(0,0,0,0.6))',
                  position: 'relative',
                  zIndex: 10,
                  opacity: isPageTransitioning ? 0 : 1,
                  transform: isPageTransitioning ? 'scale(0.93)' : 'scale(1)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
              
              {/* Featured 3D Asset (if present) (z-index: 100) */}
              {currentPage?.asset3D && (
                <>
                  <Asset3DRenderer
                    key={`3d-bilingual-${currentPageIndex}-${currentPage.asset3D.assetId}`}
                    glbUrl={currentPage.asset3D.glbUrl}
                    usdzUrl={currentPage.asset3D.usdzUrl}
                    assetId={currentPage.asset3D.assetId}
                    placement="center"
                    montessoriMode={false}
                    isPlaying={isPlaying}
                    objectSfxUrl={currentPage.objectSfxUrl}
                    isFirstAssetPage={bookData?.pages?.findIndex((p: any) => p.asset3D) === currentPageIndex}
                  />
                </>
              )}

              {/* Visual Weather Effect (z-index: 55-60) */}
              {currentPage?.visualEffect && currentPage.visualEffect.type !== 'none' && (
                <VisualWeatherEffect
                  type={currentPage.visualEffect.type}
                  intensity={currentPage.visualEffect.intensity}
                />
              )}

              {/* Magical Effect (z-index: 5 or 50) */}
              {currentPage?.magicalEffect && (
                <MagicalEffect
                  effectId={currentPage.magicalEffect.effectId}
                />
              )}

              {/* Epic Moment Effect (z-index: 70) */}
              {currentPage?.epicMoment?.enabled && (
                <EpicMomentEffect
                  effect={currentPage.epicMoment.effect}
                  trigger={currentPage.epicMoment.trigger}
                />
              )}

              {/* Compact Audio Indicator */}
              {isPlaying && (
                <div style={{
                  position: 'absolute',
                  top: 'clamp(0.75rem, 1.5vh, 1rem)',
                  left: 'clamp(0.75rem, 1.5vw, 1rem)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.375rem 0.625rem',
                  background: 'rgba(16, 185, 129, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '50px',
                  boxShadow: '0 3px 12px rgba(16, 185, 129, 0.5)',
                  animation: 'audioPlaying 1.8s ease-in-out infinite',
                  border: '1.5px solid rgba(255,255,255,0.3)',
                  zIndex: 10,
                }}>
                  <Volume2 size={14} color="white" strokeWidth={2.5} />
                  <div style={{
                    display: 'flex',
                    gap: '2px',
                    alignItems: 'flex-end',
                  }}>
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        style={{
                          width: '2.5px',
                          height: `${6 + Math.random() * 6}px`,
                          background: 'white',
                          borderRadius: '2px',
                          animation: `audioBars 1s ease-in-out infinite`,
                          animationDelay: `${i * 0.15}s`,
                        }}
                      />
                    ))}
            </div>
          </div>
              )}
        </div>

            {/* RIGHT: Compact Bilingual Text - 20% of Screen */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(0.625rem, 1.3vh, 0.875rem)',
              minHeight: 0,
              overflow: 'auto',
              paddingRight: '4px',
              justifyContent: 'center',
            }}>
              {/* Compact Source Language Card */}
              {(playMode === 'source' || playMode === 'alternating') && (
                <div style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(40px) saturate(150%)',
                  borderRadius: '18px',
                  padding: 'clamp(0.875rem, 1.8vh, 1.125rem) clamp(0.75rem, 1.6vw, 1rem)',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.5), 0 0 0 1px rgba(102, 126, 234, 0.3)',
                  border: '1.5px solid rgba(102, 126, 234, 0.25)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'clamp(0.5rem, 1vh, 0.75rem)',
                  position: 'relative',
                  opacity: isPageTransitioning ? 0 : 1,
                  transform: isPageTransitioning ? 'translateX(20px)' : 'translateX(0)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                  }}>
                    <div style={{
                      padding: '0.25rem 0.625rem',
                      background: activeAudioSource === 'source' && isPlaying
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'  // ✅ GREEN when actively playing
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '50px',
                      fontSize: 'clamp(0.625rem, 1.4vw, 0.75rem)',
                      fontWeight: 900,
                      color: 'white',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      boxShadow: activeAudioSource === 'source' && isPlaying
                        ? '0 4px 16px rgba(16, 185, 129, 0.6)'  // ✅ STRONGER glow when playing
                        : '0 2px 8px rgba(102, 126, 234, 0.4)',
                      animation: activeAudioSource === 'source' && isPlaying ? 'pulse 2s ease-in-out infinite' : 'none',
                    }}>
                      {activeAudioSource === 'source' && isPlaying ? '▶️ ' : ''}{sourceLangName}
                    </div>
                    {(playMode === 'source' || (playMode === 'alternating' && isPlaying)) && (
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#667eea',
                        animation: 'pulse 2s ease-in-out infinite',
                        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.25)',
                      }} />
                    )}
      </div>

                  <p style={{
                    fontSize: 'clamp(0.8125rem, 1.8vw, 0.9375rem)',
                    lineHeight: 1.7,
                    color: 'white',
                    margin: 0,
                    fontWeight: 500,
                    // Removed textShadow to allow WordHighlightedText karaoke effects to shine through
                    letterSpacing: '0.015em',
                    textAlign: 'left',
                    whiteSpace: 'pre-wrap',  // CRITICAL: Preserve spaces in text!
                  }}>
                    {isLearningMode ? (
                      <LetterHighlightedText
                        text={currentPage.text}
                        letterTimings={currentPage.letterTimings}
                        activeLetterIndex={activeAudioSource === 'source' ? activeLetterIndex : -1}
                        isPlaying={isPlaying}
                        activeLanguage="source"
                        showOnlySpelling={false}
                        enhancedKidsMode={bookData?.isLearningContent || false}
                      />
                    ) : (
                      <WordHighlightedText
                        text={currentPage.text}
                        wordTimings={currentPage.wordTimings}
                        currentTime={activeAudioSource === 'source' ? currentTime : undefined}
                        isPlaying={isPlaying}
                      />
                    )}
                  </p>
                </div>
              )}

              {/* Compact Target Language Card */}
              {(playMode === 'target' || playMode === 'alternating') && (
                <div style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(40px) saturate(150%)',
                  borderRadius: '18px',
                  padding: 'clamp(0.875rem, 1.8vh, 1.125rem) clamp(0.75rem, 1.6vw, 1rem)',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.5), 0 0 0 1px rgba(240, 147, 251, 0.3)',
                  border: '1.5px solid rgba(240, 147, 251, 0.25)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'clamp(0.5rem, 1vh, 0.75rem)',
                  position: 'relative',
                  opacity: isPageTransitioning ? 0 : 1,
                  transform: isPageTransitioning ? 'translateX(20px)' : 'translateX(0)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.1s',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                  }}>
                    <div style={{
                      padding: '0.25rem 0.625rem',
                      background: activeAudioSource === 'target' && isPlaying
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'  // ✅ GREEN when actively playing
                        : 'linear-gradient(135deg, #764ba2 0%, #f093fb 100%)',
                      borderRadius: '50px',
                      fontSize: 'clamp(0.625rem, 1.4vw, 0.75rem)',
                      fontWeight: 900,
                      color: 'white',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      boxShadow: activeAudioSource === 'target' && isPlaying
                        ? '0 4px 16px rgba(16, 185, 129, 0.6)'  // ✅ STRONGER glow when playing
                        : '0 2px 8px rgba(118, 75, 162, 0.4)',
                      animation: activeAudioSource === 'target' && isPlaying ? 'pulse 2s ease-in-out infinite' : 'none',
                    }}>
                      {activeAudioSource === 'target' && isPlaying ? '▶️ ' : ''}{targetLangName}
                    </div>
                    {(playMode === 'target' || (playMode === 'alternating' && isPlaying)) && (
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#f093fb',
                        animation: 'pulse 2s ease-in-out infinite',
                        boxShadow: '0 0 0 3px rgba(240, 147, 251, 0.25)',
                      }} />
                    )}
                  </div>

                  <p style={{
                    fontSize: 'clamp(0.8125rem, 1.8vw, 0.9375rem)',
                    lineHeight: 1.7,
                    color: 'white',
                    margin: 0,
                    fontWeight: 500,
                    // Removed textShadow to allow WordHighlightedText karaoke effects to shine through
                    letterSpacing: '0.015em',
                    textAlign: 'left',
                    whiteSpace: 'pre-wrap',  // CRITICAL: Preserve spaces in text!
                  }}>
                    {isLearningMode ? (
                      <LetterHighlightedText
                        text={currentPage.translatedText}
                        letterTimings={currentPage.translatedLetterTimings}
                        activeLetterIndex={activeAudioSource === 'target' ? activeLetterIndex : -1}
                        isPlaying={isPlaying}
                        activeLanguage="target"
                        showOnlySpelling={false}
                        enhancedKidsMode={bookData?.isLearningContent || false}
                      />
                    ) : (
                      <WordHighlightedText
                        text={currentPage.translatedText}
                        wordTimings={currentPage.translatedWordTimings || []}
                        currentTime={activeAudioSource === 'target' ? currentTime : undefined}
                        isPlaying={isPlaying}
                      />
                    )}
                  </p>
                </div>
              )}

              {/* Learning Mode Badge */}
              {playMode === 'alternating' && (
                <div style={{
                  padding: '0.375rem 0.75rem',
                  background: 'rgba(16, 185, 129, 0.25)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '50px',
                  border: '1.5px solid rgba(16, 185, 129, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.375rem',
                  marginTop: '0.375rem',
                }}>
                  <Languages size={13} color="white" strokeWidth={2.5} />
                  <span style={{
                    color: 'white',
                    fontSize: 'clamp(0.625rem, 1.4vw, 0.75rem)',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}>
                    Learning Mode
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Controls - Premium Glass Bar */}
          <div style={{
            padding: 'clamp(0.75rem, 1.5vh, 1rem) clamp(1.25rem, 2.5vw, 1.75rem)',
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(50px) saturate(180%)',
            borderTop: '1px solid rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(0.75rem, 1.8vw, 1rem)',
            position: 'relative',
            zIndex: 100,
            boxShadow: '0 -8px 28px rgba(0,0,0,0.3)',
          }}>
            <button
              onClick={handlePrevious}
              disabled={currentPageIndex === 0 || isPageTransitioning}  // ✅ FIX: Only disable on page 0
              aria-label="Previous page"
              style={{
                padding: 'clamp(0.75rem, 1.6vh, 0.875rem)',
                background: (currentPageIndex === 0 || isPageTransitioning)  // ✅ FIX: Consistent with disabled state
                  ? 'rgba(100, 100, 100, 0.3)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: (currentPageIndex === 0 || isPageTransitioning) ? '#999' : 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: (currentPageIndex === 0 || isPageTransitioning) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: (currentPageIndex === 0 || isPageTransitioning) ? 'none' : '0 4px 16px rgba(102, 126, 234, 0.5)',
                minWidth: 'clamp(44px, 9vw, 52px)',
                minHeight: 'clamp(44px, 9vh, 52px)',
                justifyContent: 'center',
                opacity: isPageTransitioning ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (currentPageIndex > 0 && !isPageTransitioning) {  // ✅ FIX: Enable hover for page 1+
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.04)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = (currentPageIndex === 0 || isPageTransitioning) ? 'none' : '0 4px 16px rgba(102, 126, 234, 0.5)';  // ✅ FIX
              }}
            >
              <ChevronLeft size={22} strokeWidth={2.5} />
            </button>

            {/* Play/Pause Button - FIX: Circular fullscreen design matching audio-book-viewer-unified */}
            <button
              onClick={handlePlayPause}
              aria-label={isPlaying ? 'Pause narration' : 'Play narration'}
              style={{
                width: 'clamp(56px, 7vw, 64px)',
                height: 'clamp(56px, 7vw, 64px)',
                borderRadius: '50%',  // ✅ FIX: Circular button
                border: '3px solid rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',  // ✅ FIX: Purple gradient
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.2) inset',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.3) inset';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
              }}
            >
              {/* ✅ FIX: Icon-only, no text label, larger size for fullscreen */}
              {isPlaying ? (
                <Pause size={clamp(26, 30, 34)} color="white" strokeWidth={2.5} fill="white" />
              ) : (
                <Play size={clamp(26, 30, 34)} color="white" strokeWidth={2.5} fill="white" style={{ marginLeft: '4px' }} />
              )}
            </button>

            <button
              onClick={handleNext}
              disabled={currentPageIndex === bookData.pages.length - 1 || isPageTransitioning}
              aria-label="Next page"
              style={{
                padding: 'clamp(0.75rem, 1.6vh, 0.875rem)',
                background: (currentPageIndex === bookData.pages.length - 1 || isPageTransitioning)
                  ? 'rgba(100, 100, 100, 0.3)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: (currentPageIndex === bookData.pages.length - 1 || isPageTransitioning) ? '#999' : 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: (currentPageIndex === bookData.pages.length - 1 || isPageTransitioning) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: (currentPageIndex === bookData.pages.length - 1 || isPageTransitioning) ? 'none' : '0 4px 16px rgba(102, 126, 234, 0.5)',
                minWidth: 'clamp(44px, 9vw, 52px)',
                minHeight: 'clamp(44px, 9vh, 52px)',
                justifyContent: 'center',
                opacity: isPageTransitioning ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (currentPageIndex < bookData.pages.length - 1 && !isPageTransitioning) {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.04)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = (currentPageIndex === bookData.pages.length - 1 || isPageTransitioning) ? 'none' : '0 4px 16px rgba(102, 126, 234, 0.5)';
              }}
            >
              <ChevronRight size={22} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes gradientFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }

        @keyframes audioPlaying {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes audioBars {
          0%, 100% { height: 8px; }
          50% { height: 16px; }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.25); opacity: 0.6; }
        }

        @keyframes buttonPulse {
          0%, 100% {
            transform: translateY(0) scale(1);
            box-shadow: 0 12px 30px rgba(255, 255, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.8);
          }
          50% {
            transform: translateY(-2px) scale(1.02);
            box-shadow: 0 15px 40px rgba(255, 255, 255, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.9);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes gentleBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes confettiFall {
          to {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        .story-image-hover:hover {
          transform: scale(1.02);
        }

        /* Responsive - Mobile Stack */
        @media (max-width: 1100px) {
          .bilingual-split-layout {
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
          }
          .fullscreen-layout {
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
          }
        }

        /* Tablet Fullscreen Optimization */
        @media (max-width: 1400px) {
          .fullscreen-layout {
            grid-template-columns: 65fr 35fr !important;
          }
        }

        /* Large Desktop - Even Bigger Image */
        @media (min-width: 1600px) {
          .fullscreen-layout {
            grid-template-columns: 75fr 25fr !important;
          }
        }

        /* Premium Custom Scrollbar */
        *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        *::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 10px;
        }
        *::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        *::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
          background-clip: padding-box;
        }
      `}</style>

      {/* STORY COMPLETION MODAL - Continue or Learn in New Language */}
      {showStoryCompletion && bookData && (
        <StoryCompletionModal
          isOpen={showStoryCompletion}
          onClose={() => setShowStoryCompletion(false)}
          onContinueStory={handleContinueStory}
          onTranslateStory={handleTranslateStory}
          storyTitle={bookData.title}
          currentLanguage={bookData.sourceLanguage || 'en'}
          showBilingualOption={true}
        />
      )}

      {/* SUBSCRIPTION GATE - For non-subscribers/non-owners */}
      {showSubscriptionGate && (
        <BilingualSubscriptionGate
          isAuthenticated={!!session}
          currentPath={`/audio-book/${audioBookId}`}
        />
      )}

      {/* PRO UPSELL MODAL - For learning mode requiring Pro */}
      {showProUpsellModal && (
        <ProUpsellModal
          isOpen={showProUpsellModal}
          onClose={() => {
            setShowProUpsellModal(false);
            router.push('/pricing');
          }}
        />
      )}
    </div>
  );
}

// Helper function to find the active letter index based on current time
// Used by requestAnimationFrame loop for performance optimization
function findActiveLetterIndex(currentTime: number, letterTimings: Array<{ start: number; end: number }>): number {
  if (!letterTimings || letterTimings.length === 0) return -1;
  
  for (let i = 0; i < letterTimings.length; i++) {
    if (currentTime >= letterTimings[i].start && currentTime <= letterTimings[i].end) {
      return i;
    }
  }
  
  // If we're past all letters, return last letter index
  if (currentTime > letterTimings[letterTimings.length - 1].end) {
    return letterTimings.length - 1;
  }
  
  return -1;
}
